import React, { useState } from 'react';
import { PRIORITES, STATUTS_PROJET, STATUTS_TACHE } from '../constants';
import { ChoixEntreprise, ChoixMembres, ChoixUtilisateur, Erreur, Modale } from './communs';
import { messageErreur } from '../services/api';

// Champs d'une tâche, partagés par la création (modale) et la fiche (panneau).
export function ChampsTache({ valeurs, maj, utilisateurs, sansTitre }) {
  return (
    <>
      {!sansTitre && (
        <div className="champ">
          <label htmlFor="t-titre">Titre</label>
          <input id="t-titre" required autoFocus value={valeurs.titre} onChange={(e) => maj({ titre: e.target.value })} placeholder="Ce qu’il faut faire" />
        </div>
      )}
      <div className="grille-champs">
        <div className="champ">
          <label htmlFor="t-assigne">Assignée à</label>
          <ChoixUtilisateur id="t-assigne" utilisateurs={utilisateurs} value={valeurs.assigne_id} onChange={(v) => maj({ assigne_id: v })} />
        </div>
        <div className="champ">
          <label htmlFor="t-priorite">Priorité</label>
          <select id="t-priorite" value={valeurs.priorite} onChange={(e) => maj({ priorite: e.target.value })}>
            {PRIORITES.map((p) => <option key={p.cle} value={p.cle}>{p.libelle}</option>)}
          </select>
        </div>
        <div className="champ">
          <label htmlFor="t-statut">Statut</label>
          <select id="t-statut" value={valeurs.statut} onChange={(e) => maj({ statut: e.target.value })}>
            {STATUTS_TACHE.map((s) => <option key={s.cle} value={s.cle}>{s.libelle}</option>)}
          </select>
        </div>
        <div className="champ">
          <label htmlFor="t-debut">Début</label>
          <input id="t-debut" type="date" value={valeurs.date_debut || ''} max={valeurs.date_echeance || undefined} onChange={(e) => maj({ date_debut: e.target.value || null })} />
        </div>
        <div className="champ">
          <label htmlFor="t-echeance">Échéance</label>
          <input id="t-echeance" type="date" value={valeurs.date_echeance || ''} min={valeurs.date_debut || undefined} onChange={(e) => maj({ date_echeance: e.target.value || null })} />
        </div>
      </div>
      <div className="champ">
        <label htmlFor="t-description">Description</label>
        <textarea id="t-description" rows={4} value={valeurs.description || ''} onChange={(e) => maj({ description: e.target.value })} placeholder="Contexte, attendus, liens utiles…" />
      </div>
    </>
  );
}

export const TACHE_VIDE = { titre: '', description: '', priorite: 'faible', statut: 'a_faire', assigne_id: null, date_debut: null, date_echeance: null };

// Création d'une tâche dans un projet.
export function ModaleNouvelleTache({ projetId, statut, utilisateurs, onFermer, onCree, creer }) {
  const [v, setV] = useState({ ...TACHE_VIDE, statut: statut || 'a_faire' });
  const [erreur, setErreur] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const maj = (p) => setV((x) => ({ ...x, ...p }));

  async function valider(e) {
    e.preventDefault();
    if (!v.titre.trim()) return setErreur('Le titre est obligatoire.');
    setEnvoi(true);
    try {
      const r = await creer({ ...v, projet_id: projetId });
      onCree(r.id);
    } catch (err) {
      setErreur(messageErreur(err));
      setEnvoi(false);
    }
  }

  return (
    <Modale titre="Nouvelle tâche" onFermer={onFermer}>
      <form onSubmit={valider}>
        {erreur && <Erreur>{erreur}</Erreur>}
        <ChampsTache valeurs={v} maj={maj} utilisateurs={utilisateurs} />
        <div className="aide faible" style={{ fontSize: 12, marginBottom: 12 }}>
          La personne assignée est prévenue dans l’application et par e-mail.
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn" onClick={onFermer}>Annuler</button>
          <button className="btn btn-primaire" disabled={envoi}>{envoi ? 'Création…' : 'Créer la tâche'}</button>
        </div>
      </form>
    </Modale>
  );
}

// Création ou modification d'un projet.
export function ModaleProjet({ projet, membresInitiaux = [], utilisateurs, entreprises, moi, onFermer, onEnregistre, enregistrer }) {
  const [v, setV] = useState(projet ? {
    nom: projet.nom, description: projet.description || '', entreprise_id: projet.entreprise_id,
    responsable_id: projet.responsable_id, statut: projet.statut, date_debut: projet.date_debut,
    date_echeance: projet.date_echeance, archive: projet.archive,
  } : {
    nom: '', description: '', entreprise_id: null, responsable_id: moi.id, statut: 'a_lancer',
    date_debut: null, date_echeance: null, archive: false,
  });
  const [membres, setMembres] = useState(membresInitiaux.map((m) => m.id));
  const [erreur, setErreur] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const maj = (p) => setV((x) => ({ ...x, ...p }));

  async function valider(e) {
    e.preventDefault();
    if (!v.nom.trim()) return setErreur('Le nom du projet est obligatoire.');
    setEnvoi(true);
    try {
      const r = await enregistrer({ ...v, membres });
      onEnregistre(r);
    } catch (err) {
      setErreur(messageErreur(err));
      setEnvoi(false);
    }
  }

  return (
    <Modale titre={projet ? `Projet — ${projet.nom}` : 'Nouveau projet'} onFermer={onFermer} large>
      <form onSubmit={valider}>
        {erreur && <Erreur>{erreur}</Erreur>}
        <div className="champ">
          <label htmlFor="p-nom">Nom du projet</label>
          <input id="p-nom" required autoFocus value={v.nom} onChange={(e) => maj({ nom: e.target.value })} placeholder="Ouverture de l’atelier carrosserie" />
        </div>
        <div className="grille-champs">
          <div className="champ">
            <label htmlFor="p-entreprise">Concession ou client</label>
            <ChoixEntreprise id="p-entreprise" entreprises={entreprises} value={v.entreprise_id} onChange={(x) => maj({ entreprise_id: x })} />
          </div>
          <div className="champ">
            <label htmlFor="p-responsable">Responsable</label>
            <ChoixUtilisateur id="p-responsable" utilisateurs={utilisateurs} value={v.responsable_id} onChange={(x) => maj({ responsable_id: x })} />
          </div>
          <div className="champ">
            <label htmlFor="p-debut">Début</label>
            <input id="p-debut" type="date" value={v.date_debut || ''} onChange={(e) => maj({ date_debut: e.target.value || null })} />
          </div>
          <div className="champ">
            <label htmlFor="p-echeance">Échéance</label>
            <input id="p-echeance" type="date" value={v.date_echeance || ''} onChange={(e) => maj({ date_echeance: e.target.value || null })} />
          </div>
          <div className="champ">
            <label htmlFor="p-statut">Statut</label>
            <select id="p-statut" value={v.statut} onChange={(e) => maj({ statut: e.target.value })}>
              {STATUTS_PROJET.map((s) => <option key={s.cle} value={s.cle}>{s.libelle}</option>)}
            </select>
          </div>
          {projet && (
            <div className="champ" style={{ alignSelf: 'end' }}>
              <label className="bascule"><input type="checkbox" checked={v.archive} onChange={(e) => maj({ archive: e.target.checked })} />Archivé (masqué du kanban)</label>
            </div>
          )}
        </div>
        <div className="champ">
          <label htmlFor="p-description">Description</label>
          <textarea id="p-description" rows={4} value={v.description} onChange={(e) => maj({ description: e.target.value })} placeholder="Objectif, périmètre, contraintes…" />
        </div>
        <div className="champ">
          <label htmlFor="p-membres">Membres</label>
          <ChoixMembres
            id="p-membres"
            utilisateurs={utilisateurs}
            connus={membresInitiaux}
            valeurs={membres}
            onChange={setMembres}
            responsableId={v.responsable_id}
            moiId={moi.id}
          />
          <div className="aide">Ils voient le projet et ses tâches.</div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn" onClick={onFermer}>Annuler</button>
          <button className="btn btn-primaire" disabled={envoi}>{envoi ? 'Enregistrement…' : (projet ? 'Enregistrer' : 'Créer le projet')}</button>
        </div>
      </form>
    </Modale>
  );
}
